/**
 * The second step. `pending`: the account holds a passkey or a code and this session has not
 * shown it yet — and the step offers **only what it holds**. `set_one`: a director or the
 * finance director with none set — the step is the enrollment itself, and the app does not open
 * until one is set. A passkey first; an authenticator code is the fallback.
 *
 * Sep 25: James was locked out of his own stack because this step offered "Use my passkey" on an
 * account with no passkey and a code he did not hold, with no way forward. A step that asks for
 * something the account does not have is a dead end, and there is no such thing here now.
 */
import { useState, type ReactNode } from 'react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { Button, Field, Form, Input } from '@cider/ui';
import { api, sentence } from '../../shell/api.js';
import { LoginFrame } from './Login.js';

export interface SecondFactorProps {
  mode: 'pending' | 'set_one';
  /** What this account actually holds; `pending` only ever shows one of these. */
  has?: { passkey: boolean; totp: boolean };
  onDone: () => Promise<void>;
  onSignOut: () => Promise<void>;
}

export function SecondFactor({
  mode,
  has = { passkey: true, totp: true },
  onDone,
  onSignOut,
}: SecondFactorProps): ReactNode {
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [useCode, setUseCode] = useState(false);
  const [code, setCode] = useState('');
  const [totp, setTotp] = useState<{ secret: string; uri: string } | null>(null);
  const [label, setLabel] = useState('');

  const run = async (call: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    try {
      await call();
      await onDone();
    } catch (e) {
      setRefusal(sentence(e));
    } finally {
      setBusy(false);
    }
  };

  const passkeyVerify = (): Promise<unknown> =>
    api<PublicKeyCredentialRequestOptionsJSON>('POST', '/auth/second-factor/passkey/verify/begin')
      .then((optionsJSON) => startAuthentication({ optionsJSON }))
      .then((response) => api('POST', '/auth/second-factor/passkey/verify/finish', { response }));

  const passkeyRegister = (): Promise<unknown> =>
    api<PublicKeyCredentialCreationOptionsJSON>(
      'POST',
      '/auth/second-factor/passkey/register/begin',
    )
      .then((optionsJSON) => startRegistration({ optionsJSON }))
      .then((response) =>
        api('POST', '/auth/second-factor/passkey/register/finish', {
          response,
          label: label || 'This device',
        }),
      );

  const beginTotp = async (): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    try {
      setTotp(await api<{ secret: string; uri: string }>('POST', '/auth/second-factor/totp/begin'));
      setUseCode(true);
    } catch (e) {
      setRefusal(sentence(e));
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'pending') {
    return (
      <LoginFrame>
        <h1>One more step</h1>
        <p className="charter">
          Show the passkey or the code you set. Approvals and signatures wait behind it.
        </p>
        {!useCode || !has.totp ? (
          <div className="ui-form">
            {has.passkey ? (
              <Button kind="primary" busy={busy} onClick={() => void run(passkeyVerify)}>
                Use my passkey
              </Button>
            ) : null}
            {has.totp ? (
              <Button
                kind={has.passkey ? 'secondary' : 'primary'}
                onClick={() => {
                  setUseCode(true);
                  setRefusal(null);
                }}
              >
                {has.passkey ? 'Enter a code instead' : 'Enter the code from your authenticator'}
              </Button>
            ) : null}
            {refusal ? (
              <div className="ui-form__refusal" role="alert">
                {refusal}
              </div>
            ) : null}
          </div>
        ) : (
          <Form
            busy={busy}
            refusal={refusal}
            onSubmit={() =>
              void run(() => api('POST', '/auth/second-factor/totp/verify', { code }))
            }
          >
            <Field id="code" label="The six-digit code from your authenticator app">
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9 ]{6,7}"
                required
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
              />
            </Field>
            <Button kind="primary" type="submit" busy={busy}>
              Continue
            </Button>
            {has.passkey ? (
              <Button
                onClick={() => {
                  setUseCode(false);
                }}
              >
                Use my passkey instead
              </Button>
            ) : null}
          </Form>
        )}
        <p className="login__lanes">
          Lost the {has.passkey && has.totp ? 'phone this is on' : has.passkey ? 'device' : 'app'}?
          Another director clears it — People &amp; roles → the person → Clear second factor — and
          your next sign-in sets a new one. Nobody can clear their own.
        </p>
        <p>
          <button type="button" className="login__link" onClick={() => void onSignOut()}>
            Sign out
          </button>
        </p>
      </LoginFrame>
    );
  }

  return (
    <LoginFrame>
      <h1>Set your second factor</h1>
      <p className="charter">
        Directors and the finance director approve money and sign checks, so a second factor comes
        first — a passkey, or a code from an authenticator app. A passkey is Face ID, Touch ID or
        your phone; it cannot be phished.
      </p>
      {!useCode ? (
        <Form busy={busy} refusal={refusal} onSubmit={() => void run(passkeyRegister)}>
          <Field
            id="label"
            label="What to call it"
            hint="So you recognize it later — “James’s iPhone”."
          >
            <Input
              id="label"
              value={label}
              maxLength={80}
              onChange={(e) => {
                setLabel(e.target.value);
              }}
            />
          </Field>
          <Button kind="primary" type="submit" busy={busy}>
            Create my passkey
          </Button>
          <Button busy={busy} onClick={() => void beginTotp()}>
            Use an authenticator app instead
          </Button>
        </Form>
      ) : (
        <Form
          busy={busy}
          refusal={refusal}
          onSubmit={() => void run(() => api('POST', '/auth/second-factor/totp/confirm', { code }))}
        >
          {totp ? (
            <div className="login__secret">
              <div>Add this to Google Authenticator, 1Password or Authy:</div>
              <code>{totp.secret}</code>
              <a href={totp.uri}>Open in an authenticator app</a>
            </div>
          ) : null}
          <Field id="code2" label="Then the six-digit code it shows">
            <Input
              id="code2"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
              }}
            />
          </Field>
          <Button kind="primary" type="submit" busy={busy}>
            Confirm
          </Button>
        </Form>
      )}
      <p>
        <button type="button" className="login__link" onClick={() => void onSignOut()}>
          Sign out
        </button>
      </p>
    </LoginFrame>
  );
}
